/**
 * ComposerCollectIntake — the asset intake (upload + landing-page URL) rendered
 * INSIDE the chat composer instead of inside the assistant bubble, ChatGPT-style:
 * staged attachments show as chips at the top of the input, an "Upload" affordance
 * and a URL field sit on the action row, and a single "Add to study" button submits.
 * That button also accepts a URL still sitting unstaged in the field: it stages it
 * and submits once the capture lands, so pasting a link and pressing submit works
 * without first discovering the +. YouTube / player-page links are refused here.
 *
 * It owns the same intake state the CollectCard used to (files + parallel URL
 * captures via useUrlCaptures) and hands the raw values up through onCollect; the
 * parent still owns upload + receipt. The matching CollectCard bubble collapses to
 * its prompt text while this is mounted, so the intake lives in exactly one place.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FILES_PER_ASSET, messageLinks } from "@digital-twins/shared-types";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  FileText,
  Loader2,
  Paperclip,
  Play,
  Plus,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { cn } from "@/lib/utils";
import { renderPdfFirstPagePreview } from "@/lib/utils/extract-pdf-text";
import {
  friendlyCaptureError,
  useUrlCaptures,
  videoUrlFromCapture,
} from "@/components/media/useUrlCaptures";
import type { IssuedAssetFile } from "@/lib/api/client";
import { captureQuestionnaireUrlApi } from "../api/questionnaireDraftsApi";
import { CapturedImageChip } from "@/components/media/CapturedImageChip";
import { CreativeMedia } from "@/components/chat/CreativeMedia";
import { CreativeLightbox } from "@/components/chat/CreativeLightbox";

import { acceptStagedFiles } from "../assets/acceptStagedFiles";
import {
  ASSET_MEDIA_ACCEPT,
  isAcceptedAssetFile,
  isSupportedVideoMime,
} from "../assets/assetMediaAccept";
import type { ChatMessageCollectAttachItem } from "@digital-twins/shared-types";

import { ChatMessageCollect } from "../types";

/**
 * `m:ss` for a staged video, or null while no usable duration is known: metadata
 * has not landed, the source is a still image, or the browser handed back NaN /
 * Infinity (a stream). Text, never an icon: a marketer weighing three cuts of the
 * same ad reads the length, and an icon says nothing about it.
 */
function formatDurationLabel(durationSec: number | undefined): string | null {
  if (
    durationSec === undefined ||
    !Number.isFinite(durationSec) ||
    durationSec <= 0
  ) {
    return null;
  }
  const totalSeconds = Math.round(durationSec);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

interface ComposerCollectIntakeProps {
  collect: ChatMessageCollect;
  /**
   * The assets this card has already collected and saved while its question
   * is open: a receipt turn that failed or was stopped left them saved. They
   * count against the card's limit, and "Add to study" with nothing new
   * staged sends the receipt for them instead of asking for them again.
   */
  collectedCount?: number;
  /**
   * An attach card's items still without their file (F19), in the card's
   * order. Each item takes the family registry's `filesPerAsset`
   * (`FILES_PER_ASSET`): a staged file goes on one of them, its
   * chip names the item and a small select in the chip changes it. Given
   * more files than items, the card says so, the user picks the file each
   * item uses, and the others stay out, named. The card then takes files
   * only, and with nothing staged or saved its button says it continues
   * without one. Absent on a card whose files are the items.
   */
  attachItems?: ReadonlyArray<ChatMessageCollectAttachItem>;
  /**
   * Links the field starts with: the ones the user's message before the card
   * holds, on a card that collects a family made from a link (ads, pages).
   * Nothing is captured until the user presses "Add to study": the press is
   * the decision (lead decision on the QA4 review, option 2). They are the
   * card's offer only while it has no files: once a file is attached for it,
   * the field shows what the user typed, never the offer (LP-05).
   */
  initialLinks?: ReadonlyArray<string>;
  /**
   * Files the card starts with, staged as if picked here: the ones the user
   * attached in the chat composer before the card opened (B2: the composer's
   * attach takes this one upload path). Nothing is uploaded until the user
   * presses "Add to study".
   */
  initialFiles?: ReadonlyArray<File>;
  /** Disable all interactions (true while a turn is in flight). */
  disabled: boolean;
  /**
   * Raw values handed up; the parent owns upload + receipt. Resolves false
   * when nothing was collected; the staged intake stays. On an attach card,
   * `targets` is the item each file goes on, file by file.
   */
  onCollect: (values: {
    urls: Array<string>;
    files: Array<File>;
    /** Pages captured for their preview, each issued as a file (D2). */
    captured?: Array<{ url: string; file: IssuedAssetFile }>;
    targets?: Array<ChatMessageCollectAttachItem>;
  }) => Promise<boolean | void>;
  /**
   * Whether the user has staged something not added yet (files, links, or a
   * link still in the field). The page's leave guard reads it: leaving would
   * lose it, and nothing else in the intake is unsaved.
   */
  onStagedChange?: (staged: boolean) => void;
}

export function ComposerCollectIntake(props: ComposerCollectIntakeProps) {
  const {
    attachItems,
    collect,
    collectedCount = 0,
    disabled,
    initialFiles,
    initialLinks,
    onCollect,
    onStagedChange,
  } = props;
  const { t } = useTranslation("app");

  // An attach card holds the registry's `filesPerAsset` per item still
  // without one; its saved files are on items it no longer offers.
  const attaching = attachItems !== undefined;
  const maxAssets = attaching
    ? attachItems.length * FILES_PER_ASSET
    : (collect.maxAssets ?? Number.POSITIVE_INFINITY);
  const hasAssetLimit = attaching || collect.maxAssets !== undefined;

  // Staged as a pick here stages them (`acceptFiles`): an attach card keeps
  // every one, any other card up to its room.
  const [files, setFiles] = useState<Array<File>>(() => {
    const given = (initialFiles ?? []).filter(isAcceptedAssetFile);
    return attaching
      ? given
      : acceptStagedFiles({
          prevFiles: [],
          incoming: given,
          captureCount: collectedCount,
          maxAssets,
        });
  });
  const [previews, setPreviews] = useState<Array<string | null>>([]);
  const [submitting, setSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [urlText, setUrlText] = useState(() =>
    (initialLinks ?? []).join(" "),
  );
  // The field's text is the user's once they type in it or stage its links;
  // until then it holds the card's offer, which a file attached for the card
  // withdraws (typed state, never the links' text): submitted beside the
  // files, the offered link would add the same page twice (LP-05).
  const [urlTyped, setUrlTyped] = useState(false);
  const urlDraft =
    !urlTyped && collectedCount + files.length > 0 ? "" : urlText;
  const setUrlDraft = useCallback((value: string) => {
    setUrlTyped(true);
    setUrlText(value);
  }, []);
  /**
   * The capture the submit button is waiting on. Set when submit is pressed with
   * a URL still sitting in the field: the URL is staged and the submission
   * finishes once that capture lands (instant for YouTube, a few seconds for a
   * landing-page screenshot).
   */
  const [pendingCaptureIds, setPendingCaptureIds] = useState<Array<string>>(
    [],
  );
  /**
   * Which staged creative the lightbox is showing, held as that creative's own
   * preview src (its identity), or null while it is closed. Without this the
   * chips were dead tiles: an attached video could never be played back.
   *
   * Identity, not position: preview srcs fill in asynchronously (a PDF thumbnail
   * lands well after the image/video object URLs), so a position captured at
   * click time silently pointed at a different creative the moment a later
   * preview joined the list ahead of it.
   */
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  /**
   * Video durations reported by CreativeMedia once metadata lands, keyed by the
   * creative's own preview src rather than by its position. Same reason the
   * lightbox tracks identity: removing a chip renumbers every chip after it, and
   * an index-keyed map would then label a video with its neighbour's runtime.
   */
  const [durationsBySrc, setDurationsBySrc] = useState<Record<string, number>>(
    {},
  );
  /** An attach card's items the user picked for a file, by file (F19). */
  const [chosenTargets, setChosenTargets] = useState<Map<File, number>>(
    () => new Map(),
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  // A recipe may cap this one collection surface. Absence is intentionally
  // unlimited here; it must not become a hidden study-wide roster ceiling.
  const captures = useUrlCaptures({
    capture: captureQuestionnaireUrlApi,
    scale: 1,
    max: maxAssets,
    allowYoutubeVideo: false,
  });
  const readyCaptures = captures.captures.filter((c) => c.status === "ready");
  // Each failed capture's line names its link: two links failing read as two
  // lines, never one reason that names neither (LP-09, LP-22).
  const failedCaptures = captures.captures.filter(
    (capture) => capture.status === "error" && capture.error,
  );
  const anyCapturing = captures.captures.some((c) => c.status === "loading");
  // A failed capture is no asset, so it takes no room (`useUrlCaptures`).
  const heldCaptureCount = captures.captures.length - failedCaptures.length;
  const totalAssets = attaching
    ? files.length
    : collectedCount + files.length + heldCaptureCount;

  const locked = disabled || submitting;
  const atCapacity = totalAssets >= maxAssets;
  // A drop on a full attach card still lands: the card asks which file each
  // item uses instead of ignoring the new one.
  const takesDrop = !locked && (attaching || !atCapacity);
  // An attach card (F19) and an optional one (B2) can always go on. With
  // nothing staged and nothing saved, the button says so in words: it
  // continues without a file.
  const passable = attaching || collect.optional === true;
  const continuesWithout =
    passable &&
    files.length === 0 &&
    collectedCount === 0 &&
    readyCaptures.length === 0;
  const hasContent =
    passable ||
    collectedCount > 0 ||
    files.length > 0 ||
    readyCaptures.length > 0;

  /**
   * More files staged than the card's items take: the user picks which file
   * each item uses, and the rest stay out. Never the first ones by position
   * (BP-01: a second press photo vanished).
   */
  const overflowing = attaching && files.length > maxAssets;

  /**
   * The item each staged file goes on, file by file: the one the user picked
   * in its chip. With one item and the files it takes there is nothing to
   * pick, so they go on it; otherwise a file has no item until the user
   * picks one. An item takes `FILES_PER_ASSET`, so picking a full item swaps
   * this file with one it holds.
   */
  const fileTargets = useMemo(() => {
    if (!attaching) return [];
    const targets: Array<number | undefined> = [];
    const held = new Map<number, number>();
    files.forEach((file, index) => {
      const chosen =
        attachItems.length === 1 && !overflowing
          ? 0
          : chosenTargets.get(file);
      if (
        chosen !== undefined &&
        chosen < attachItems.length &&
        (held.get(chosen) ?? 0) < FILES_PER_ASSET
      ) {
        targets[index] = chosen;
        held.set(chosen, (held.get(chosen) ?? 0) + 1);
      } else {
        targets[index] = undefined;
      }
    });
    return targets;
  }, [attaching, attachItems, files, chosenTargets, overflowing]);
  /**
   * The card waits for the user's picks: every staged file needs its item,
   * or, with more files than the items take, every item needs its files.
   */
  const pickedCount = fileTargets.filter((target) => target !== undefined).length;
  const awaitingPick =
    attaching && pickedCount < Math.min(files.length, maxAssets);
  /** Files no item takes once every item has its pick: they stay out. */
  const leftOutFiles =
    overflowing && !awaitingPick
      ? files.filter((_, index) => fileTargets[index] === undefined)
      : [];

  const chooseTarget = useCallback(
    (index: number, item: number) => {
      setChosenTargets((prev) => {
        const next = new Map(prev);
        const holders = fileTargets.flatMap((target, k) =>
          k !== index && target === item ? [k] : [],
        );
        const other = holders.length >= FILES_PER_ASSET ? holders[0] : -1;
        if (other >= 0) {
          const swapped = fileTargets[index];
          if (swapped === undefined) next.delete(files[other]);
          else next.set(files[other], swapped);
        }
        next.set(files[index], item);
        return next;
      });
    },
    [fileTargets, files],
  );
  /**
   * A URL typed but not yet staged still counts as something to submit. Without
   * this the arrow sits disabled while a perfectly good URL is on screen, and
   * the only way through is the small + (or Enter), which nobody finds.
   */
  const hasUrlDraft = urlDraft.trim().length > 0;
  const staged = files.length > 0 || captures.count > 0 || hasUrlDraft;
  useEffect(() => {
    onStagedChange?.(staged);
  }, [onStagedChange, staged]);
  // Gone with the intake: an answered card has nothing staged.
  useEffect(() => () => onStagedChange?.(false), [onStagedChange]);

  // The field takes the links it holds by URL syntax (`messageLinks`: a link
  // has no spaces), whatever words label them ("Ours: … Apple's: …"), each
  // staged up to the card's room. With no link in it, nothing is captured and
  // the field says so (null: the submit waits for a link or an empty field).
  // The chat composer keeps `linksOnly`: a message with words there goes to
  // the model.
  const [notALink, setNotALink] = useState(false);
  const addCurrentUrl = useCallback((): Array<string> | null => {
    const typed = urlDraft.trim();
    if (!typed || locked || totalAssets >= maxAssets) return [];
    const links = messageLinks(typed);
    setNotALink(links.length === 0);
    if (links.length === 0) return null;
    const ids = links
      .slice(0, maxAssets - totalAssets)
      .map((url) => captures.addUrl(url))
      .filter((id): id is string => Boolean(id));
    setUrlDraft("");
    return ids;
  }, [urlDraft, locked, totalAssets, maxAssets, captures, setUrlDraft]);

  useEffect(() => {
    let cancelled = false;
    const objectUrls: Array<string> = [];
    setPreviews(files.map(() => null));

    files.forEach((file, index) => {
      // Videos preview through an object URL too — the chip hands that URL to
      // CreativeMedia, which renders a <video> tile (never an <img>, which would
      // show broken for a video source) and falls back to a labelled placeholder
      // when the browser cannot decode the container.
      if (file.type.startsWith("image/") || isSupportedVideoMime(file.type)) {
        const objectUrl = URL.createObjectURL(file);
        objectUrls.push(objectUrl);
        setPreviews((current) =>
          current.map((preview, i) => (i === index ? objectUrl : preview)),
        );
        return;
      }
      if (file.type === "application/pdf") {
        void renderPdfFirstPagePreview(file, 240)
          .then((previewUrl) => {
            if (!cancelled) {
              setPreviews((current) =>
                current.map((preview, i) =>
                  i === index ? previewUrl : preview,
                ),
              );
            }
          })
          .catch(() => {
            // Keep the document icon fallback if PDF rendering is unavailable.
          });
      }
    });

    return () => {
      cancelled = true;
      objectUrls.forEach((objectUrl) => URL.revokeObjectURL(objectUrl));
    };
  }, [files]);

  const acceptFiles = useCallback(
    (incoming: Array<File>) => {
      const supported = incoming.filter(isAcceptedAssetFile);
      if (supported.length < incoming.length) {
        // Never silently swallow a picked file: name what the intake takes.
        toast.error(
          "Only images, PDFs, and MP4, WebM, or MOV videos are supported.",
        );
      }
      if (supported.length === 0) return;
      setFiles((prev) =>
        // An attach card keeps every file given: past its items, the user
        // picks the ones to use and the card names the rest.
        attaching
          ? [...prev, ...supported]
          : acceptStagedFiles({
              prevFiles: prev,
              incoming: supported,
              // The saved ones count against the card's limit too.
              captureCount: heldCaptureCount + collectedCount,
              maxAssets,
            }),
      );
    },
    [attaching, maxAssets, heldCaptureCount, collectedCount],
  );

  const handleSubmit = useCallback(async () => {
    if (locked || !hasContent || awaitingPick) return;
    setSubmitting(true);
    try {
      // A direct video file URL travels as a URL: it attaches as a video
      // upstream. A page travels as the capture its preview was issued, so it
      // is never captured again.
      const videoUrls = captures.captures
        .map((c) => videoUrlFromCapture(c))
        .filter((url): url is string => Boolean(url));
      const captured = captures.captures.flatMap((c) =>
        c.status === "ready" && c.result?.file
          ? [{ url: c.url, file: c.result.file }]
          : [],
      );
      // An attach card sends each picked file with its item; a file no item
      // took stays out.
      const placed = attaching
        ? files.flatMap((file, index) => {
            const target = fileTargets[index];
            return target === undefined
              ? []
              : [{ file, item: attachItems[target] }];
          })
        : [];
      const collected = await onCollect({
        urls: videoUrls,
        files: attaching ? placed.map(({ file }) => file) : files,
        ...(captured.length ? { captured } : {}),
        ...(attaching ? { targets: placed.map(({ item }) => item) } : {}),
      });
      // Nothing collected ("Keep editing"): keep the staged intake.
      if (collected === false) return;
      setFiles([]);
      setChosenTargets(new Map());
      captures.clear();
    } catch {
      // Parent surfaced the error (toast); keep the staged intake for a retry.
    } finally {
      setSubmitting(false);
    }
  }, [
    locked,
    hasContent,
    awaitingPick,
    onCollect,
    files,
    captures,
    attaching,
    attachItems,
    fileTargets,
  ]);

  /**
   * What the submit button actually does. A URL still in the field is staged
   * first and the submission resumes in the effect below once that capture
   * settles, so one press is enough whether the marketer typed a URL, attached
   * a file, or both.
   */
  const requestSubmit = useCallback(() => {
    if (locked) return;
    if (hasUrlDraft) {
      const ids = addCurrentUrl();
      if (ids === null) return;
      if (ids.length > 0) {
        setPendingCaptureIds(ids);
        return;
      }
    }
    if (!hasContent) return;
    void handleSubmit();
  }, [locked, hasUrlDraft, addCurrentUrl, hasContent, handleSubmit]);

  // Resume a submission that was waiting on a freshly staged URL. A capture that
  // failed stops here instead: its chip carries the reason plus retry/remove, and
  // submitting anyway would wipe that chip and silently drop the URL.
  useEffect(() => {
    if (pendingCaptureIds.length === 0) return;
    const pending = captures.captures.filter((c) =>
      pendingCaptureIds.includes(c.id),
    );
    if (pending.some((c) => c.status === "loading")) return;
    setPendingCaptureIds([]);
    if (pending.some((c) => c.status === "error")) return;
    void handleSubmit();
  }, [pendingCaptureIds, captures.captures, handleSubmit]);

  const busy = submitting || anyCapturing;

  /**
   * Only chips that actually have a preview src are openable; the lightbox steps
   * through this list with its arrows. The list is rebuilt every render and grows
   * as previews resolve, which is why the open creative is tracked by src.
   */
  const lightboxCreatives = files.flatMap((file, index) => {
    const src = previews[index];
    if (!src) {
      return [];
    }
    return [{ src, mimeType: file.type || undefined }];
  });

  /**
   * Where the open creative sits in the list as it stands right now. -1 means it
   * is no longer there (removed, or the list emptied), which reads as closed.
   */
  const lightboxIndex =
    lightboxSrc === null
      ? -1
      : lightboxCreatives.findIndex((creative) => creative.src === lightboxSrc);

  // Drop the stored identity once its creative leaves the list, so a creative
  // staged later cannot re-open the lightbox uninvited.
  useEffect(() => {
    if (lightboxSrc !== null && lightboxIndex === -1) {
      setLightboxSrc(null);
    }
  }, [lightboxSrc, lightboxIndex]);

  return (
    <div
      className="flex flex-col gap-2.5"
      data-testid="composer-collect-intake"
      onDragOver={(e) => {
        if (Array.from(e.dataTransfer?.types ?? []).includes("Files")) {
          e.preventDefault();
          if (takesDrop) setDragActive(true);
        }
      }}
      onDragLeave={(e) => {
        // Only clear when the drag actually leaves the intake, not on child enter.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setDragActive(false);
        }
      }}
      onDrop={(e) => {
        if (Array.from(e.dataTransfer?.types ?? []).includes("Files")) {
          e.preventDefault();
          setDragActive(false);
          if (!takesDrop) return;
          acceptFiles(Array.from(e.dataTransfer.files ?? []));
        }
      }}
    >
      {/* Staged attachments — chips at the top of the input, ChatGPT-style.
          A chip with a preview is a button that opens the shared CreativeLightbox
          (native video controls, arrows across the staged creatives); the remove
          control is a positioned sibling of it, never a nested button.

          A gallery, not a grid of squares: three video ads tested head to head are
          three different shapes, and a 64px centre crop rendered a vertical cut as
          an unreadable slice of itself. Each tile is a fixed 96px tall with an
          automatic width, so the browser sizes it from the creative's own intrinsic
          ratio once metadata lands (no JS measurement), and the row scrolls
          sideways instead of wrapping, so a large legitimate roster does not push
          the composer's own controls off screen.

          The horizontal padding is what keeps the scroller from clipping a remove
          control: CapturedImageChip hangs its own at -right-1 -top-1, outside the
          tile bounds, and overflow-x-auto clips at the padding box. */}
      {(files.length > 0 || captures.count > 0) && (
        <div
          data-testid="composer-collect-chips"
          className="flex flex-nowrap items-center gap-2 overflow-x-auto px-1 py-1"
        >
          {captures.captures.map((capture) => (
            <CapturedImageChip
              key={capture.id}
              capture={capture}
              onRedo={() => captures.redo(capture.id)}
              onValidate={() => captures.validate(capture.id)}
              onRemove={() => captures.remove(capture.id)}
            />
          ))}
          {files.map((file, index) => {
            const previewSrc = previews[index];
            const isVideo = isSupportedVideoMime(file.type);
            const durationLabel = previewSrc
              ? formatDurationLabel(durationsBySrc[previewSrc])
              : null;
            const leftOut = leftOutFiles.includes(file);
            return (
              <div
                key={`${file.name}-${index}`}
                className={cn(
                  "flex shrink-0 flex-col items-start gap-1",
                  leftOut && "opacity-50",
                )}
              >
                <div
                  // min-width, so a chip still resolving its preview (or showing the
                  // PDF icon) holds a tile instead of collapsing to nothing; media
                  // narrower than it (a full-page screenshot) covers it from its
                  // top rather than showing a sliver (U9).
                  className="relative h-24 w-auto min-w-[4rem] shrink-0 overflow-hidden rounded-lg border border-border bg-background/60"
                >
                  {previewSrc ? (
                    <button
                      type="button"
                      data-testid={`composer-collect-file-chip-${index}`}
                      title={file.name}
                      aria-label={`Open ${file.name}`}
                      onClick={() =>
                        // Identity, not position: this chip only renders with a src.
                        setLightboxSrc(previewSrc)
                      }
                      className="relative block h-full w-auto cursor-zoom-in transition-opacity hover:opacity-90"
                    >
                      <CreativeMedia
                        src={previewSrc}
                        mimeType={file.type}
                        alt={file.name}
                        className="h-full w-full object-cover object-top"
                        onMeta={(meta) => {
                          setDurationsBySrc((current) => {
                            if (current[previewSrc] === meta.durationSec) {
                              return current;
                            }
                            return {
                              ...current,
                              [previewSrc]: meta.durationSec,
                            };
                          });
                        }}
                        playOnHover={isVideo}
                      />
                      {isVideo ? (
                        <span
                          aria-hidden
                          className="pointer-events-none absolute inset-0 flex items-center justify-center"
                        >
                          <Play
                            className="h-3.5 w-3.5 text-white drop-shadow"
                            fill="currentColor"
                          />
                        </span>
                      ) : null}
                      {durationLabel ? (
                        <span
                          aria-hidden
                          data-testid={`composer-collect-file-duration-${index}`}
                          className="pointer-events-none absolute bottom-1 left-1 rounded bg-black/70 px-1 py-0.5 text-[10px] font-medium leading-none text-white"
                        >
                          {durationLabel}
                        </span>
                      ) : null}
                    </button>
                  ) : (
                    <div
                      aria-label={file.name}
                      data-testid={`composer-collect-file-chip-${index}`}
                      className="flex h-full w-full items-center justify-center bg-background/60 text-twin-chat-fg-muted"
                    >
                      <FileText className="h-6 w-6" />
                    </div>
                  )}
                  <button
                    type="button"
                    aria-label={`Remove ${file.name}`}
                    data-testid={`composer-collect-remove-file-${index}`}
                    disabled={locked}
                    onClick={() =>
                      setFiles((prev) => prev.filter((_, i) => i !== index))
                    }
                    // Inside the tile, never overhanging it: the scroller clips at its
                    // padding box, and an outward-positioned control would lose its
                    // corner the moment the row overflows.
                    className="absolute right-1 top-1 rounded-full bg-background/90 p-0.5 text-twin-chat-fg-muted transition-colors hover:text-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
                {attaching ? (
                  // The item this file goes on (F19), changed right here.
                  <select
                    data-testid={`composer-collect-file-target-${index}`}
                    aria-label={t("questionnaire.attachCard.fileFor", {
                      file: file.name,
                    })}
                    value={fileTargets[index] ?? ""}
                    disabled={locked}
                    onChange={(event) =>
                      chooseTarget(index, Number(event.target.value))
                    }
                    className="max-w-[12rem] truncate rounded-md border border-border bg-background px-1.5 py-0.5 text-xs text-twin-chat-fg disabled:opacity-60"
                  >
                    {fileTargets[index] === undefined ? (
                      <option value="" disabled>
                        {leftOut
                          ? t("questionnaire.attachCard.notAdded")
                          : t("questionnaire.attachCard.pickItem")}
                      </option>
                    ) : null}
                    {attachItems.map((item, itemIndex) => (
                      <option key={item.assetKeys.join("|")} value={itemIndex}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {/* More files than items (F19: one each): the limit and the pick it
          asks for, then the files that stay out, by name. */}
      {overflowing ? (
        <p
          data-testid="composer-collect-attach-limit"
          className="text-sm text-twin-chat-fg-muted"
        >
          {leftOutFiles.length > 0
            ? t("questionnaire.attachCard.leftOut", {
                count: leftOutFiles.length,
                files: leftOutFiles.map((file) => file.name).join(", "),
              })
            : attachItems.length === 1
              ? t("questionnaire.attachCard.pickFileOne", {
                  item: attachItems[0].label,
                })
              : t("questionnaire.attachCard.pickFileMany")}
        </p>
      ) : null}

      {failedCaptures.length > 0 || notALink ? (
        <div
          data-testid="composer-collect-capture-error"
          className="flex flex-col gap-1"
        >
          {notALink ? (
            <p className="text-sm text-[#E5484D]">
              {friendlyCaptureError("invalid_url", null)}
            </p>
          ) : null}
          {failedCaptures.map((capture) => (
            <p key={capture.id} className="text-sm text-[#E5484D]">
              <span className="break-all">{capture.url}</span> — {capture.error}
            </p>
          ))}
        </div>
      ) : null}

      {/* URL line — landing pages become screenshots; direct video files
          attach as video. YouTube / player-page links are refused. Enter or
          the + stages a URL without submitting (for adding several); the submit
          arrow stages and submits in one press. */}
      {attaching ? null : (
        <div className="flex items-center gap-2">
          <input
            type="url"
            inputMode="url"
            data-testid="composer-collect-url-input"
            value={urlDraft}
            disabled={locked || atCapacity}
            onChange={(e) => {
              setNotALink(false);
              setUrlDraft(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addCurrentUrl();
              }
            }}
            placeholder={
              atCapacity
                ? t("questionnaire.maxAssetsAdded", { count: maxAssets })
                : hasAssetLimit
                  ? t("questionnaire.pasteUrlsOrAttach", { count: maxAssets })
                  : t("questionnaire.pasteUrlsOrAttachAny")
            }
            className="min-w-0 flex-1 bg-transparent text-base text-twin-chat-fg outline-none placeholder:text-twin-chat-fg-muted/50 disabled:opacity-60"
          />
          {urlDraft.trim() ? (
            <button
              type="button"
              data-testid="composer-collect-add-url"
              disabled={locked || atCapacity}
              onClick={() => addCurrentUrl()}
              title={t("questionnaire.captureUrlTitle")}
              aria-label={t("questionnaire.captureUrlAria")}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-twin-chat-fg-muted transition-colors hover:bg-twin-chat-accent/10 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
            >
              <Plus className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      )}

      {/* Action row: Upload affordance (bottom-left) + submit (bottom-right). */}
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          data-testid="composer-collect-upload"
          disabled={locked || atCapacity}
          onClick={() => fileInputRef.current?.click()}
          aria-label={t("questionnaire.attachFiles")}
          title={
            atCapacity
              ? t("questionnaire.maxAssetsAdded", { count: maxAssets })
              : totalAssets > 0
                ? hasAssetLimit
                  ? t("questionnaire.assetsAddedOf", {
                      current: totalAssets,
                      max: maxAssets,
                    })
                  : t("questionnaire.assetsAdded", { count: totalAssets })
                : t("questionnaire.attachFiles")
          }
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full bg-muted px-3.5 py-1.5 text-base font-medium text-twin-chat-fg-muted transition-colors",
            !locked && !atCapacity && "hover:bg-muted/70 hover:text-foreground",
            (locked || atCapacity) && "cursor-not-allowed opacity-50",
            dragActive && "bg-twin-chat-accent/10 text-foreground",
          )}
        >
          <Paperclip className="h-4 w-4" />
          {t("questionnaire.attach")}
        </button>

        <button
          type="button"
          data-testid="composer-collect-submit"
          disabled={
            locked ||
            (!hasContent && !hasUrlDraft) ||
            anyCapturing ||
            awaitingPick
          }
          onClick={requestSubmit}
          aria-label={
            continuesWithout
              ? t("questionnaire.continueWithoutFile")
              : t("questionnaire.addToStudy")
          }
          className={cn(
            "flex h-[32px] items-center justify-center gap-1.5 rounded-[8px] bg-cta text-cta-foreground transition-opacity disabled:opacity-40",
            // Said in words, not only on an arrow: a touch screen has no
            // tooltip, and this press adds nothing.
            continuesWithout ? "px-3 text-sm font-medium" : "w-[32px]",
          )}
        >
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : continuesWithout ? (
            t("questionnaire.continueWithoutFile")
          ) : (
            <ArrowRight className="h-4 w-4" />
          )}
        </button>
      </div>

      {lightboxIndex >= 0 && (
        <CreativeLightbox
          creatives={lightboxCreatives}
          index={lightboxIndex}
          onIndex={(next) =>
            setLightboxSrc(lightboxCreatives[next]?.src ?? null)
          }
          onClose={() => setLightboxSrc(null)}
          dataTestId="composer-collect-preview-overlay"
        />
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={ASSET_MEDIA_ACCEPT}
        // An attach card takes its items' files: one item of one file, one file.
        multiple={!attaching || maxAssets > 1}
        className="sr-only"
        data-testid="composer-collect-file-input"
        onChange={(e) => {
          acceptFiles(Array.from(e.target.files ?? []));
          e.target.value = "";
        }}
      />
    </div>
  );
}
